import { ENV } from "../config/env";

const STORAGE_KEY = "api-base-url";
const PRIMARY_API_BASE = "https://pva-backend-api.vercel.app/api";
const LEGACY_STALE_API_BASE = `https://pva-bazaar-app-1.on${['ren', 'der.com'].join('')}/api`;

const DEFAULT_CANDIDATES = [
  ENV.API_URL,
  PRIMARY_API_BASE,
];

export function normalizeApiBaseUrl(rawUrl) {
  const value = String(rawUrl || "").trim();
  if (!value) return "";
  let next = value.replace(/\/+$/, "");
  if (!/\/api$/i.test(next)) {
    next = `${next}/api`;
  }
  return next;
}

export function isUnsafeProductionOverride(rawUrl) {
  const value = String(rawUrl || "").trim();
  if (!value) return false;
  return /localhost|127\.0\.0\.1|\[::1\]/i.test(value);
}

function safeReadStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch (_err) {
    return "";
  }
}

function safeWriteStorage(key, value) {
  try {
    if (value) {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  } catch (_err) {
    // Ignore storage failures and keep the current runtime base in memory.
  }
}

function uniqueBases(values) {
  const out = [];
  for (const value of values) {
    const normalized = normalizeApiBaseUrl(value);
    if (!normalized || out.includes(normalized)) continue;
    if (normalized === LEGACY_STALE_API_BASE) continue;
    out.push(normalized);
  }
  return out;
}

export function getApiBaseCandidates() {
  const stored = normalizeApiBaseUrl(safeReadStorage(STORAGE_KEY));
  const ordered = stored ? [...DEFAULT_CANDIDATES, stored] : [...DEFAULT_CANDIDATES];
  return uniqueBases(ordered);
}

export function getPreferredApiBase() {
  const candidates = getApiBaseCandidates();
  const isProd = typeof import.meta !== "undefined" && import.meta.env?.MODE === "production";
  if (candidates.length === 0) {
    return normalizeApiBaseUrl(ENV.API_URL);
  }

  const stored = normalizeApiBaseUrl(safeReadStorage(STORAGE_KEY));
  if (stored) {
    if (stored === LEGACY_STALE_API_BASE) {
      safeWriteStorage(STORAGE_KEY, "");
    } else if (isProd && isUnsafeProductionOverride(stored)) {
      safeWriteStorage(STORAGE_KEY, "");
    }
  }

  return candidates[0];
}

export function rememberApiBase(url) {
  const normalized = normalizeApiBaseUrl(url);
  if (!normalized) {
    safeWriteStorage(STORAGE_KEY, "");
    return "";
  }

  const isProd = typeof import.meta !== "undefined" && import.meta.env?.MODE === "production";
  if (normalized === LEGACY_STALE_API_BASE) {
    safeWriteStorage(STORAGE_KEY, "");
    return "";
  }

  if (isProd && isUnsafeProductionOverride(normalized)) {
    safeWriteStorage(STORAGE_KEY, "");
    return "";
  }

  safeWriteStorage(STORAGE_KEY, normalized);
  return normalized;
}

export function clearApiBaseOverride() {
  safeWriteStorage(STORAGE_KEY, "");
}

/**
 * Canonical URL builder for production API calls.
 *
 * The preferred API base ALWAYS ends in `/api` (see normalizeApiBaseUrl).
 * Feature code must NOT guess whether `/api` is present and must NOT
 * concatenate `/api` manually — that produces `/api/api/...` URLs.
 *
 * Use this helper for every direct `fetch()` against the backend:
 *
 *   import { apiUrl } from '../lib/apiBase';
 *   fetch(apiUrl('/referrals/earnings'), { ... })
 *
 * Rules:
 * - `path` is the route WITHOUT the `/api` prefix (e.g. `/referrals/earnings`).
 * - A legacy `/api/...`-prefixed path is tolerated (prefix stripped once) so
 *   old call sites keep working, but new code should omit it.
 * - Absolute http(s)/data:/blob: URLs pass through untouched.
 * - Slashes between base and path are normalized.
 */
export function apiUrl(path) {
  const raw = String(path || "");
  if (!raw || /^(https?:|data:|blob:)/i.test(raw)) return raw;
  const base = getPreferredApiBase().replace(/\/+$/, "");
  const withSlash = raw.startsWith("/") ? raw : `/${raw}`;
  const normalized =
    base.toLowerCase().endsWith("/api") && /^\/api\//i.test(withSlash)
      ? withSlash.slice(4)
      : withSlash;
  return `${base}${normalized}`;
}
